import React from 'react'
import { Check, Clock, FileDown, FileMinus, Plus, X, Download, Send } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { apiFetch, getAccessToken, openPdfInNewTab } from '../../lib/api-client'

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS_DA = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']

const TABS = [
  { key: 'fakturaer', label: 'Fakturaer' },
  { key: 'betalingssporing', label: 'Betalingssporing' },
  { key: 'export', label: 'Export' },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDKK(oere: number): string {
  return (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr'
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface LeaseOption {
  id: string
  unit_no: string
  property_address: string
}

interface RenterOption {
  id: string
  name: string
  email: string
}

interface LineItem {
  description: string
  amount_oere: number
}

interface Invoice {
  id: string
  invoice_number?: string | null
  title: string
  description?: string | null
  type: string
  status: string
  amount_oere: number
  issue_date: string
  due_date?: string | null
  lease_id?: string | null
  renter_id?: string | null
  renter_name?: string | null
  line_items?: LineItem[]
}

interface InvoicesResponse {
  items: Invoice[]
  total: number
}

interface LedgerMonth {
  year: number
  month: number
  status: string
  expected_oere: number
  received_oere: number
}

interface LedgerRow {
  lease_id: string
  unit_no: string
  address: string
  renter_name: string
  months: LedgerMonth[]
  payment_id?: string
}

// ─── Opkrævninger types ────────────────────────────────────────────────────────

interface OpkraevningPreviewRow {
  lease_id: string
  lease_unit_no: string
  lease_address: string
  renter_name: string
  renter_email: string | null
  monthly_rent_oere: number
  aconto_varme_oere: number
  aconto_vand_oere: number
  aconto_el_oere: number
  total_oere: number
  forfaldsdato: string | null
  laatste_rettidige: string | null
  digital_kommunikation_aftalt: boolean
}

interface GenerateResult {
  generated: number
  sent: number
  skipped: number
}

// ─── Month helpers ─────────────────────────────────────────────────────────────

const MONTHS_DA_FULL = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December']

function getMonthOptions(): Array<{ value: string; label: string }> {
  const now = new Date()
  const options: Array<{ value: string; label: string }> = []
  for (let offset = -1; offset <= 1; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    options.push({ value: `${yyyy}-${mm}`, label: `${MONTHS_DA_FULL[d.getMonth()]} ${yyyy}` })
  }
  return options
}

// ─── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  React.useEffect(() => {
    const t = setTimeout(onClose, 4000)
    return () => clearTimeout(t)
  }, [onClose])
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 999,
      background: '#16a34a', color: '#fff', borderRadius: 8,
      padding: '12px 20px', fontSize: 13, fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'center', gap: 12,
      maxWidth: 400,
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: 0, lineHeight: 1 }}>
        <X size={14} />
      </button>
    </div>
  )
}

// ─── Opkrævninger modal ────────────────────────────────────────────────────────

interface OpkraevningerModalProps {
  onClose: () => void
  onGenerated: () => void
}

function OpkraevningerModal({ onClose, onGenerated }: OpkraevningerModalProps) {
  const monthOptions = getMonthOptions()
  const [step, setStep] = React.useState<1 | 2>(1)
  const [selectedMonth, setSelectedMonth] = React.useState(monthOptions[1]?.value ?? monthOptions[0].value)
  const [previewRows, setPreviewRows] = React.useState<OpkraevningPreviewRow[]>([])
  const [loadingPreview, setLoadingPreview] = React.useState(false)
  const [previewError, setPreviewError] = React.useState<string | null>(null)
  const [sendEmails, setSendEmails] = React.useState(true)
  const [generating, setGenerating] = React.useState(false)
  const [generateError, setGenerateError] = React.useState<string | null>(null)

  async function loadPreview() {
    setLoadingPreview(true)
    setPreviewError(null)
    try {
      // The endpoint returns { month, count, previews: [...] } — not a bare array.
      const res = await apiFetch<OpkraevningPreviewRow[] | { previews: OpkraevningPreviewRow[] }>(`/api/v1/opkraevninger/preview?month=${selectedMonth}`)
      setPreviewRows(Array.isArray(res) ? res : (res.previews ?? []))
      setStep(2)
    } catch (e: unknown) {
      setPreviewError(e instanceof Error ? e.message : 'Kunne ikke hente forhåndsvisning.')
    } finally {
      setLoadingPreview(false)
    }
  }

  async function generate() {
    setGenerating(true)
    setGenerateError(null)
    try {
      const result = await apiFetch<GenerateResult>('/api/v1/opkraevninger/generate', {
        method: 'POST',
        body: JSON.stringify({ month: selectedMonth, send_emails: sendEmails }),
      })
      onGenerated()
      onClose()
      return result
    } catch (e: unknown) {
      setGenerateError(e instanceof Error ? e.message : 'Kunne ikke generere opkrævninger.')
      return null
    } finally {
      setGenerating(false)
    }
  }

  const totalOere = previewRows.reduce((sum, r) => sum + r.total_oere, 0)

  function emailStatusIcon(row: OpkraevningPreviewRow): React.ReactNode {
    if (!row.digital_kommunikation_aftalt) return <span title="Digital kommunikation ikke aftalt" style={{ color: '#9ca3af' }}>⊘</span>
    if (!row.renter_email) return <span title="Ingen email" style={{ color: '#dc2626' }}>✗</span>
    return <span title={row.renter_email} style={{ color: '#16a34a' }}>✓</span>
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#fff', borderRadius: 10, width: '100%',
        maxWidth: step === 2 ? 760 : 440,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #e8eaed' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Generer opkrævninger</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {step === 1 ? 'Trin 1 af 2 — Vælg måned' : 'Trin 2 af 2 — Forhåndsvis og bekræft'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: 24, flex: 1 }}>
          {step === 1 && (
            <>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Vælg måned</label>
                <select
                  style={{ ...inputStyle, maxWidth: 280 }}
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                >
                  {monthOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
                Der genereres én opkrævning per aktivt lejeforhold for den valgte måned.
                Opkrævninger sendes via e-mail til lejere med digital kommunikationsaftale.
              </div>
              {previewError && (
                <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginTop: 16, border: '1px solid #fecaca' }}>
                  {previewError}
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              {/* Summary */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  <strong style={{ color: '#111827' }}>{previewRows.length}</strong> opkrævninger ·{' '}
                  <strong style={{ color: '#111827' }}>{formatDKK(totalOere)}</strong> i alt
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#111827', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={sendEmails}
                    onChange={e => setSendEmails(e.target.checked)}
                    style={{ width: 15, height: 15, cursor: 'pointer' }}
                  />
                  Send via e-mail
                </label>
              </div>

              {previewRows.length === 0 ? (
                <div style={{ padding: '32px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  Ingen aktive lejeforhold for den valgte måned.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {['Lejemål', 'Lejer', 'Grundleje', 'Aconto', 'Total', 'Email'].map(h => (
                        <th key={h} style={{
                          padding: '8px 12px', textAlign: h === 'Email' ? 'center' : 'left',
                          fontSize: 11, fontWeight: 600, color: '#6b7280',
                          textTransform: 'uppercase', letterSpacing: '0.05em',
                          borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => {
                      const acontoOere = row.aconto_varme_oere + row.aconto_vand_oere + row.aconto_el_oere
                      const noEmail = !row.digital_kommunikation_aftalt || !row.renter_email
                      return (
                        <tr key={row.lease_id} style={{
                          borderBottom: i === previewRows.length - 1 ? 'none' : '1px solid #f3f4f6',
                          opacity: noEmail && sendEmails ? 0.7 : 1,
                        }}>
                          <td style={{ padding: '9px 12px', fontWeight: 500, color: '#111827' }}>
                            <div>{row.lease_unit_no}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af' }}>{row.lease_address}</div>
                          </td>
                          <td style={{ padding: '9px 12px', color: '#6b7280' }}>{row.renter_name}</td>
                          <td style={{ padding: '9px 12px', color: '#111827', fontFamily: 'monospace', fontSize: 12 }}>
                            {formatDKK(row.monthly_rent_oere)}
                          </td>
                          <td style={{ padding: '9px 12px', color: '#111827', fontFamily: 'monospace', fontSize: 12 }}>
                            {acontoOere > 0 ? formatDKK(acontoOere) : <span style={{ color: '#d1d5db' }}>—</span>}
                          </td>
                          <td style={{ padding: '9px 12px', color: '#111827', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>
                            {formatDKK(row.total_oere)}
                          </td>
                          <td style={{ padding: '9px 12px', textAlign: 'center', fontSize: 16 }}>
                            {emailStatusIcon(row)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}

              {generateError && (
                <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginTop: 16, border: '1px solid #fecaca' }}>
                  {generateError}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e8eaed', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div>
            {step === 2 && (
              <Button variant="secondary" size="sm" onClick={() => setStep(1)}>← Tilbage</Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={onClose}>Annuller</Button>
            {step === 1 ? (
              <Button size="sm" isLoading={loadingPreview} onClick={loadPreview}>
                Forhåndsvis →
              </Button>
            ) : (
              <Button size="sm" isLoading={generating} onClick={generate}>
                <Send size={13} />
                Generer & send
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Invoice helpers ──────────────────────────────────────────────────────────

function invoiceStatusLabel(s: string): string {
  const map: Record<string, string> = {
    draft: 'Kladde',
    sent: 'Sendt',
    paid: 'Betalt',
    overdue: 'Forfalden',
    cancelled: 'Annulleret',
  }
  return map[s] ?? s
}

function invoiceTypeLabel(t: string): string {
  const map: Record<string, string> = {
    rent: 'Husleje',
    aconto_settlement: 'Acontoafregning',
    deposit_claim: 'Depositumskrav',
    extra_charge: 'Ekstraopkrævning',
    credit_note: 'Kreditnota',
  }
  return map[t] ?? t
}

// ─── Payment helpers ──────────────────────────────────────────────────────────

function paymentCellBg(status: string): string {
  switch (status) {
    case 'paid': return '#f0fdf4'
    case 'partial': return '#fffbeb'
    case 'overdue': return '#fef2f2'
    default: return '#f9fafb'
  }
}

function paymentStatusLabel(s: string): string {
  const map: Record<string, string> = {
    paid: 'Betalt',
    partial: 'Delvis',
    overdue: 'Forfalden',
    pending: 'Afventer',
  }
  return map[s] ?? s
}

// ─── Month columns for the ledger ────────────────────────────────────────────

function getLedgerColumns(): Array<{ year: number; month: number }> {
  const now = new Date()
  const cols: Array<{ year: number; month: number }> = []
  // 3 months back + current + 2 months forward = 6 columns
  for (let offset = -3; offset <= 2; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1)
    cols.push({ year: d.getFullYear(), month: d.getMonth() + 1 })
  }
  return cols
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 1: Fakturaer
// ═══════════════════════════════════════════════════════════════════════════════

const STATUS_FILTERS = [
  { key: '', label: 'Alle' },
  { key: 'draft', label: 'Kladde' },
  { key: 'sent', label: 'Sendt' },
  { key: 'paid', label: 'Betalt' },
  { key: 'overdue', label: 'Forfalden' },
]

interface InvoiceModalProps {
  invoice?: Invoice | null
  leases: LeaseOption[]
  renters: RenterOption[]
  onClose: () => void
  onSaved: () => void
}

function InvoiceModal({ invoice, leases, renters, onClose, onSaved }: InvoiceModalProps) {
  const isEdit = !!invoice
  const [form, setForm] = React.useState({
    lease_id: invoice?.lease_id ?? '',
    renter_id: invoice?.renter_id ?? '',
    type: invoice?.type ?? 'rent',
    title: invoice?.title ?? '',
    description: invoice?.description ?? '',
    issue_date: invoice?.issue_date ?? todayISO(),
    due_date: invoice?.due_date ?? '',
    status: invoice?.status ?? 'draft',
  })
  const [lineItems, setLineItems] = React.useState<Array<{ description: string; amount: string }>>(
    invoice?.line_items?.map(li => ({
      description: li.description,
      amount: (li.amount_oere / 100).toFixed(2),
    })) ?? [{ description: '', amount: '' }]
  )
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const totalOere = lineItems.reduce((sum, li) => {
    const val = parseFloat(li.amount.replace(',', '.')) || 0
    return sum + Math.round(val * 100)
  }, 0)

  function set(field: string, val: string) {
    setForm(f => ({ ...f, [field]: val }))
  }

  function updateLineItem(idx: number, field: 'description' | 'amount', val: string) {
    setLineItems(items => items.map((li, i) => i === idx ? { ...li, [field]: val } : li))
  }

  function addLineItem() {
    setLineItems(items => [...items, { description: '', amount: '' }])
  }

  function removeLineItem(idx: number) {
    setLineItems(items => items.filter((_, i) => i !== idx))
  }

  async function save(markSent: boolean) {
    setSaving(true)
    setError(null)
    try {
      const body = {
        lease_id: form.lease_id || undefined,
        renter_id: form.renter_id || undefined,
        type: form.type,
        title: form.title,
        description: form.description || undefined,
        amount_oere: totalOere,
        line_items: lineItems
          .filter(li => li.description || li.amount)
          .map(li => ({
            description: li.description,
            amount_oere: Math.round((parseFloat(li.amount.replace(',', '.')) || 0) * 100),
          })),
        issue_date: form.issue_date,
        due_date: form.due_date || undefined,
        status: markSent ? 'sent' : form.status,
      }
      if (isEdit && invoice) {
        await apiFetch(`/api/v1/invoices/${invoice.id}`, { method: 'PATCH', body: JSON.stringify(body) })
      } else {
        await apiFetch('/api/v1/invoices', { method: 'POST', body: JSON.stringify(body) })
      }
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Kunne ikke gemme faktura.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#fff', borderRadius: 10, width: '100%', maxWidth: 560,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #e8eaed' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
            {isEdit ? 'Faktura' : 'Ny faktura'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: 24, flex: 1 }}>
          {error && <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginBottom: 16, border: '1px solid #fecaca' }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Lejemål</label>
              <select style={inputStyle} value={form.lease_id} onChange={e => set('lease_id', e.target.value)}>
                <option value="">— Vælg lejemål —</option>
                {leases.map(l => <option key={l.id} value={l.id}>{l.unit_no} · {l.property_address}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Lejer</label>
              <select style={inputStyle} value={form.renter_id} onChange={e => set('renter_id', e.target.value)}>
                <option value="">— Vælg lejer —</option>
                {renters.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Type</label>
            <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="rent">Husleje</option>
              <option value="aconto_settlement">Acontoafregning</option>
              <option value="deposit_claim">Depositumskrav</option>
              <option value="extra_charge">Ekstraopkrævning</option>
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Titel</label>
            <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} placeholder="F.eks. Acontoafregning vand – maj 2026" />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Beskrivelse (valgfri)</label>
            <textarea style={{ ...inputStyle, height: 72, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Udstedelsesdato</label>
              <input type="date" style={inputStyle} value={form.issue_date} onChange={e => set('issue_date', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Forfaldsdato</label>
              <input type="date" style={inputStyle} value={form.due_date} onChange={e => set('due_date', e.target.value)} />
            </div>
          </div>

          {/* Line items */}
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>Linjer</label>
            {lineItems.map((li, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="Beskrivelse"
                  value={li.description}
                  onChange={e => updateLineItem(idx, 'description', e.target.value)}
                />
                <input
                  style={{ ...inputStyle, width: 110 }}
                  placeholder="Beløb (kr)"
                  value={li.amount}
                  onChange={e => updateLineItem(idx, 'amount', e.target.value)}
                />
                <button onClick={() => removeLineItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 4px', flexShrink: 0 }}>
                  <X size={14} />
                </button>
              </div>
            ))}
            <button onClick={addLineItem} style={{ background: 'none', border: '1px dashed #cbd5e1', borderRadius: 6, color: '#64748b', fontSize: 12, padding: '5px 12px', cursor: 'pointer', width: '100%' }}>
              + Tilføj linje
            </button>
          </div>

          <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 700, color: '#111827', marginTop: 12 }}>
            Total: {formatDKK(totalOere)}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e8eaed', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onClose}>Annuller</Button>
          <Button variant="secondary" size="sm" isLoading={saving} onClick={() => save(false)}>Gem som kladde</Button>
          <Button size="sm" isLoading={saving} onClick={() => save(true)}>Gem og marker sendt</Button>
        </div>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5,
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#111827',
  background: '#fff', fontFamily: 'inherit', outline: 'none',
}

function FakturaerTab() {
  const [invoices, setInvoices] = React.useState<Invoice[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [statusFilter, setStatusFilter] = React.useState('')
  const [showModal, setShowModal] = React.useState(false)
  const [selectedInvoice, setSelectedInvoice] = React.useState<Invoice | null>(null)
  const [leases, setLeases] = React.useState<LeaseOption[]>([])
  const [renters, setRenters] = React.useState<RenterOption[]>([])
  const [showOpkraevningerModal, setShowOpkraevningerModal] = React.useState(false)
  const [toast, setToast] = React.useState<string | null>(null)

  const fetchInvoices = React.useCallback(() => {
    setLoading(true)
    const qs = statusFilter ? `?status=${statusFilter}` : ''
    apiFetch<InvoicesResponse>(`/api/v1/invoices${qs}`)
      .then(r => setInvoices(r.items))
      .catch(() => setError('Kunne ikke hente fakturaer.'))
      .finally(() => setLoading(false))
  }, [statusFilter])

  React.useEffect(() => { (async () => { await fetchInvoices() })() }, [fetchInvoices])

  React.useEffect(() => {
    apiFetch<LeaseOption[]>('/api/v1/leases').then(setLeases).catch(() => {})
    apiFetch<RenterOption[]>('/api/v1/renters').then(setRenters).catch(() => {})
  }, [])

  async function downloadPdf(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    try {
      await openPdfInNewTab(`/api/v1/invoices/${id}/pdf`)
    } catch {
      alert('Kunne ikke hente PDF.')
    }
  }

  async function markPaid(inv: Invoice, e: React.MouseEvent) {
    e.stopPropagation()
    const label = inv.invoice_number ? `${inv.invoice_number} · ${formatDKK(inv.amount_oere)}` : `${inv.title} · ${formatDKK(inv.amount_oere)}`
    if (!window.confirm(`Markér faktura som betalt?\n\n${label}\n\nDette opretter også bogføringsposter (DR 6900 Bank / CR 5810 Tilgodehavende lejere).`)) return
    try {
      await apiFetch(`/api/v1/opkraevninger/${inv.id}/mark-paid`, {
        method: 'PATCH',
        body: JSON.stringify({ paid_at: todayISO(), received_oere: inv.amount_oere }),
      })
      setToast('Faktura markeret som betalt.')
      fetchInvoices()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Kunne ikke markere fakturaen som betalt.')
    }
  }

  async function sendToRenter(inv: Invoice, e: React.MouseEvent) {
    e.stopPropagation()
    const label = inv.invoice_number ? `${inv.invoice_number} · ${formatDKK(inv.amount_oere)}` : `${inv.title} · ${formatDKK(inv.amount_oere)}`
    if (!window.confirm(`Send faktura til lejer?\n\n${label}\n\nFakturaen sendes som PDF til lejerens e-mail.`)) return
    try {
      await apiFetch(`/api/v1/opkraevninger/${inv.id}/send-email`, { method: 'POST' })
      setToast('Faktura sendt til lejer.')
      fetchInvoices()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Kunne ikke sende fakturaen til lejer.')
    }
  }

  async function createCreditNote(inv: Invoice, e: React.MouseEvent) {
    e.stopPropagation()
    const label = inv.invoice_number ? `${inv.invoice_number} · ${formatDKK(inv.amount_oere)}` : `${inv.title} · ${formatDKK(inv.amount_oere)}`
    if (!window.confirm(`Opret kreditnota for hele beløbet?\n\n${label}\n\nDer oprettes en kreditnota, der modposterer den oprindelige faktura.`)) return
    try {
      await apiFetch(`/api/v1/invoices/${inv.id}/credit-note`, { method: 'POST', body: JSON.stringify({}) })
      setToast('Kreditnota oprettet.')
      fetchInvoices()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Kunne ikke oprette kreditnota.')
    }
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid #e8eaed' }}>
        <div style={{ display: 'flex', gap: 5, overflowX: 'auto' }}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: statusFilter === f.key ? 600 : 500,
                background: statusFilter === f.key ? '#4f46e5' : '#f7f8fa',
                color: statusFilter === f.key ? '#ffffff' : '#6b7280',
                border: statusFilter === f.key ? '1px solid #4f46e5' : '1px solid #e8eaed',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.12s',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={() => setShowOpkraevningerModal(true)}>
              <Send size={13} />
              Generer opkrævninger
            </Button>
            <Button size="sm" onClick={() => { setSelectedInvoice(null); setShowModal(true) }}>
              <Plus size={13} />
              Ny faktura
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#9ca3af' }}>
            <Clock size={11} />
            Automatisk generering kører den 20. i måneden
          </div>
        </div>
      </div>

      {/* Table */}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>}
      {error && <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>}
      {!loading && !error && invoices.length === 0 && (
        <div style={{ padding: 56, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#9ca3af' }}>Ingen fakturaer fundet.</div>
        </div>
      )}
      {!loading && !error && invoices.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Fakturanr.', 'Titel', 'Lejer', 'Beløb', 'Udstedt', 'Forfald', 'Status', ''].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv, i) => (
              <InvoiceRow
                key={inv.id}
                invoice={inv}
                isLast={i === invoices.length - 1}
                onClick={() => { setSelectedInvoice(inv); setShowModal(true) }}
                onDownloadPdf={downloadPdf}
                onMarkPaid={markPaid}
                onSendEmail={sendToRenter}
                onCreateCreditNote={createCreditNote}
              />
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <InvoiceModal
          invoice={selectedInvoice}
          leases={leases}
          renters={renters}
          onClose={() => setShowModal(false)}
          onSaved={fetchInvoices}
        />
      )}

      {showOpkraevningerModal && (
        <OpkraevningerModal
          onClose={() => setShowOpkraevningerModal(false)}
          onGenerated={() => {
            setToast('Opkrævninger er genereret og sendt.')
            fetchInvoices()
          }}
        />
      )}

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  )
}

function InvoiceStatusChip({ status }: { status: string }) {
  const styles: Record<string, React.CSSProperties> = {
    draft: { background: '#f3f4f6', color: '#6b7280', border: '1px solid #e8eaed' },
    sent: { background: '#eff6ff', color: '#4f46e5', border: '1px solid #dbeafe' },
    paid: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
    overdue: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
    cancelled: { background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e8eaed', textDecoration: 'line-through' },
  }
  const s = styles[status] ?? styles.draft
  return (
    <span style={{
      ...s,
      borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 500,
      display: 'inline-block', lineHeight: '18px',
    }}>
      {invoiceStatusLabel(status)}
    </span>
  )
}

function InvoiceRow({ invoice: inv, isLast, onClick, onDownloadPdf, onMarkPaid, onSendEmail, onCreateCreditNote }: {
  invoice: Invoice
  isLast: boolean
  onClick: () => void
  onDownloadPdf: (id: string, e: React.MouseEvent) => void
  onMarkPaid: (invoice: Invoice, e: React.MouseEvent) => void
  onSendEmail: (invoice: Invoice, e: React.MouseEvent) => void
  onCreateCreditNote: (invoice: Invoice, e: React.MouseEvent) => void
}) {
  const [hovered, setHovered] = React.useState(false)
  const cancelled = inv.status === 'cancelled'
  const isCreditNote = inv.type === 'credit_note'
  const canMarkPaid = inv.status !== 'paid' && inv.status !== 'cancelled'
  const canSend = !cancelled && Boolean(inv.lease_id || inv.renter_id)
  const canCreditNote = !cancelled && !isCreditNote

  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? '#f9fafb' : 'transparent', cursor: 'pointer', borderBottom: isLast ? 'none' : '1px solid #f3f4f6', transition: 'background 0.1s' }}
    >
      <td style={{ padding: '12px 16px', color: '#4f46e5', fontFamily: 'monospace', fontSize: 12 }}>
        {inv.invoice_number ?? '—'}
      </td>
      <td style={{ padding: '12px 16px', fontWeight: 500, color: '#111827', textDecoration: cancelled ? 'line-through' : 'none' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {inv.title}
          {isCreditNote && (
            <span style={{
              background: '#faf5ff', color: '#7c3aed', border: '1px solid #e9d5ff',
              borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600,
              textTransform: 'none', letterSpacing: 0, whiteSpace: 'nowrap', textDecoration: 'none',
            }}>
              {invoiceTypeLabel('credit_note')}
            </span>
          )}
        </span>
      </td>
      <td style={{ padding: '12px 16px', color: '#6b7280' }}>
        {inv.renter_name ?? '—'}
      </td>
      <td style={{ padding: '12px 16px', color: '#111827', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>
        {formatDKK(inv.amount_oere)}
      </td>
      <td style={{ padding: '12px 16px', color: '#6b7280' }}>{fmtDate(inv.issue_date)}</td>
      <td style={{ padding: '12px 16px', color: '#6b7280' }}>{fmtDate(inv.due_date)}</td>
      <td style={{ padding: '12px 16px' }}>
        <InvoiceStatusChip status={inv.status} />
      </td>
      <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
        {canCreditNote && (
          <button
            onClick={e => onCreateCreditNote(inv, e)}
            title="Opret kreditnota"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', padding: 4, marginRight: 4, display: 'inline-flex', alignItems: 'center' }}
          >
            <FileMinus size={14} />
          </button>
        )}
        {canSend && (
          <button
            onClick={e => onSendEmail(inv, e)}
            title="Send til lejer"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', padding: 4, marginRight: 4, display: 'inline-flex', alignItems: 'center' }}
          >
            <Send size={14} />
          </button>
        )}
        {canMarkPaid && (
          <button
            onClick={e => onMarkPaid(inv, e)}
            title="Markér betalt"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', padding: 4, marginRight: 4, display: 'inline-flex', alignItems: 'center' }}
          >
            <Check size={14} />
          </button>
        )}
        <button
          onClick={e => onDownloadPdf(inv.id, e)}
          title="Hent PDF"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, display: 'inline-flex', alignItems: 'center' }}
        >
          <FileDown size={14} />
        </button>
      </td>
    </tr>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 2: Betalingssporing
// ═══════════════════════════════════════════════════════════════════════════════

interface PaymentPopoverProps {
  row: LedgerRow
  col: { year: number; month: number }
  existing: LedgerMonth | null
  onClose: () => void
  onSaved: () => void
}

function PaymentPopover({ row, col, existing, onClose, onSaved }: PaymentPopoverProps) {
  const [receivedDKK, setReceivedDKK] = React.useState(
    existing ? (existing.received_oere / 100).toFixed(2) : ''
  )
  const [notes, setNotes] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function markPaid() {
    setSaving(true)
    setError(null)
    try {
      const received_oere = Math.round((parseFloat(receivedDKK.replace(',', '.')) || 0) * 100)
      if (existing && row.payment_id) {
        await apiFetch(`/api/v1/rent-payments/${row.payment_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'paid', received_oere, notes: notes || undefined }),
        })
      } else {
        await apiFetch('/api/v1/rent-payments', {
          method: 'POST',
          body: JSON.stringify({
            lease_id: row.lease_id,
            period_year: col.year,
            period_month: col.month,
            expected_oere: existing ? existing.expected_oere : received_oere,
            received_oere,
            status: 'paid',
            notes: notes || undefined,
          }),
        })
      }
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Fejl ved gemning.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: 300, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', border: '1px solid #e8eaed' }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 4 }}>
          {row.unit_no} — {MONTHS_DA[col.month - 1]} {col.year}
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>{row.renter_name}</div>

        {error && <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 12, padding: '6px 10px', borderRadius: 6, marginBottom: 12, border: '1px solid #fecaca' }}>{error}</div>}

        {existing && (
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
            Forventet: <strong>{formatDKK(existing.expected_oere)}</strong> · Status: {paymentStatusLabel(existing.status)}
          </div>
        )}

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Modtaget beløb (kr)</label>
          <input style={inputStyle} value={receivedDKK} onChange={e => setReceivedDKK(e.target.value)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Notat</label>
          <input style={inputStyle} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Valgfri note" />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={onClose}>Luk</Button>
          <Button size="sm" isLoading={saving} onClick={markPaid}>Marker som betalt</Button>
        </div>
      </div>
    </div>
  )
}

interface CreatePaymentModalProps {
  leases: LeaseOption[]
  onClose: () => void
  onSaved: () => void
}

function CreatePaymentModal({ leases, onClose, onSaved }: CreatePaymentModalProps) {
  const now = new Date()
  const [form, setForm] = React.useState({
    lease_id: '',
    period_year: String(now.getFullYear()),
    period_month: String(now.getMonth() + 1),
    expected_dkk: '',
    received_dkk: '',
    status: 'pending',
    notes: '',
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  function set(field: string, val: string) { setForm(f => ({ ...f, [field]: val })) }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await apiFetch('/api/v1/rent-payments', {
        method: 'POST',
        body: JSON.stringify({
          lease_id: form.lease_id,
          period_year: parseInt(form.period_year),
          period_month: parseInt(form.period_month),
          expected_oere: Math.round((parseFloat(form.expected_dkk.replace(',', '.')) || 0) * 100),
          received_oere: form.received_dkk ? Math.round((parseFloat(form.received_dkk.replace(',', '.')) || 0) * 100) : undefined,
          status: form.status,
          notes: form.notes || undefined,
        }),
      })
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Kunne ikke oprette betalingspost.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#fff', borderRadius: 10, width: 440, boxShadow: '0 8px 40px rgba(0,0,0,0.15)', border: '1px solid #e8eaed' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #e8eaed' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Opret betalingspost</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}><X size={16} /></button>
        </div>
        <div style={{ padding: 24 }}>
          {error && <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginBottom: 16, border: '1px solid #fecaca' }}>{error}</div>}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Lejemål</label>
            <select style={inputStyle} value={form.lease_id} onChange={e => set('lease_id', e.target.value)}>
              <option value="">— Vælg lejemål —</option>
              {leases.map(l => <option key={l.id} value={l.id}>{l.unit_no} · {l.property_address}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>År</label>
              <input style={inputStyle} value={form.period_year} onChange={e => set('period_year', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Måned (1–12)</label>
              <select style={inputStyle} value={form.period_month} onChange={e => set('period_month', e.target.value)}>
                {MONTHS_DA.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Forventet (kr)</label>
              <input style={inputStyle} value={form.expected_dkk} onChange={e => set('expected_dkk', e.target.value)} placeholder="5000.00" />
            </div>
            <div>
              <label style={labelStyle}>Modtaget (kr)</label>
              <input style={inputStyle} value={form.received_dkk} onChange={e => set('received_dkk', e.target.value)} placeholder="Valgfri" />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Status</label>
            <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="pending">Afventer</option>
              <option value="paid">Betalt</option>
              <option value="partial">Delvis</option>
              <option value="overdue">Forfalden</option>
            </select>
          </div>
          <div style={{ marginBottom: 4 }}>
            <label style={labelStyle}>Notat</label>
            <input style={inputStyle} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e8eaed', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onClose}>Annuller</Button>
          <Button size="sm" isLoading={saving} onClick={save}>Opret</Button>
        </div>
      </div>
    </div>
  )
}

function BetalingssporingTab() {
  const [ledger, setLedger] = React.useState<LedgerRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [popover, setPopover] = React.useState<{ row: LedgerRow; col: { year: number; month: number }; existing: LedgerMonth | null } | null>(null)
  const [showCreate, setShowCreate] = React.useState(false)
  const [leases, setLeases] = React.useState<LeaseOption[]>([])

  const cols = getLedgerColumns()

  const fetchLedger = React.useCallback(() => {
    setLoading(true)
    apiFetch<LedgerRow[]>('/api/v1/rent-payments/ledger')
      .then(setLedger)
      .catch(() => setError('Kunne ikke hente betalingsoversigt.'))
      .finally(() => setLoading(false))
  }, [])

  React.useEffect(() => { (async () => { await fetchLedger() })() }, [fetchLedger])
  React.useEffect(() => { (async () => { await apiFetch<LeaseOption[]>('/api/v1/leases').then(setLeases).catch(() => {}) })() }, [])

  function openCell(row: LedgerRow, col: { year: number; month: number }) {
    const existing = row.months?.find(m => m.year === col.year && m.month === col.month) ?? null
    setPopover({ row, col, existing })
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Henter…</div>
  if (error) return <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px', borderBottom: '1px solid #e8eaed' }}>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus size={13} />
          Opret betalingspost
        </Button>
      </div>

      {ledger.length === 0 && (
        <div style={{ padding: 56, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#9ca3af' }}>Ingen betalingsdata endnu.</div>
        </div>
      )}

      {ledger.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ ...ledgerThStyle, position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1, minWidth: 190 }}>Lejemål / Lejer</th>
                {cols.map(c => (
                  <th key={`${c.year}-${c.month}`} style={{ ...ledgerThStyle, textAlign: 'center', minWidth: 110 }}>
                    {MONTHS_DA[c.month - 1]} {c.year}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ledger.map((row, ri) => (
                <tr key={row.lease_id} style={{ borderBottom: ri === ledger.length - 1 ? 'none' : '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 16px', position: 'sticky', left: 0, background: '#fff', zIndex: 1, borderRight: '1px solid #e8eaed' }}>
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: 12 }}>{row.unit_no}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 155 }}>{row.address}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{row.renter_name}</div>
                  </td>
                  {cols.map(col => {
                    const m = row.months?.find(mo => mo.year === col.year && mo.month === col.month)
                    const bg = m ? paymentCellBg(m.status) : '#fff'
                    return (
                      <td
                        key={`${col.year}-${col.month}`}
                        onClick={() => openCell(row, col)}
                        style={{ padding: '7px 10px', textAlign: 'center', background: bg, cursor: 'pointer', verticalAlign: 'middle', transition: 'filter 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(0.96)')}
                        onMouseLeave={e => (e.currentTarget.style.filter = '')}
                      >
                        {m ? (
                          <>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#111827' }}>
                              {formatDKK(m.received_oere)}
                            </div>
                            <div style={{ fontSize: 10, color: '#6b7280' }}>
                              / {formatDKK(m.expected_oere)}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {popover && (
        <PaymentPopover
          row={popover.row}
          col={popover.col}
          existing={popover.existing}
          onClose={() => setPopover(null)}
          onSaved={fetchLedger}
        />
      )}
      {showCreate && (
        <CreatePaymentModal
          leases={leases}
          onClose={() => setShowCreate(false)}
          onSaved={fetchLedger}
        />
      )}
    </div>
  )
}

const ledgerThStyle: React.CSSProperties = {
  padding: '10px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em',
  borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap',
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 3: Export
// ═══════════════════════════════════════════════════════════════════════════════

function ExportTab() {
  const [downloading, setDownloading] = React.useState(false)
  const [downloadingEconomic, setDownloadingEconomic] = React.useState(false)
  const [fromDate, setFromDate] = React.useState('')
  const [toDate, setToDate] = React.useState('')

  async function downloadCSV() {
    setDownloading(true)
    try {
      const token = getAccessToken()
      const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
      const resp = await fetch(`${apiUrl}/api/v1/rent-payments/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error('Hentning fejlede')
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `betalingsoversigt-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      alert('Kunne ikke hente CSV-filen.')
    } finally {
      setDownloading(false)
    }
  }

  async function downloadEconomicCSV() {
    setDownloadingEconomic(true)
    try {
      const token = getAccessToken()
      const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
      const params = new URLSearchParams()
      if (fromDate) params.set('from_date', fromDate)
      if (toDate) params.set('to_date', toDate)
      const qs = params.toString() ? `?${params.toString()}` : ''
      const resp = await fetch(`${apiUrl}/api/v1/invoices/export-economic.csv${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error('Hentning fejlede')
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `e-conomic-fakturaer-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      alert('Kunne ikke hente e-conomic CSV-filen.')
    } finally {
      setDownloadingEconomic(false)
    }
  }

  return (
    <div style={{ padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 0, maxWidth: 560 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 4 }}>Betalingsoversigt</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
          Hent alle betalingsposter som CSV-fil (UTF-8 BOM, Excel-kompatibel).
        </div>
        <Button size="sm" isLoading={downloading} onClick={downloadCSV}>
          <Download size={13} />
          Hent betalingsoversigt (CSV)
        </Button>
      </div>

      <div style={{ borderTop: '1px solid #e8eaed', paddingTop: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 4 }}>e-conomic faktura-export</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Export af fakturaer i e-conomic-format (semikolon-separeret, dansk talformat, UTF-8 BOM).
          Momsfri huslejeindtægt — moms altid 0,00.
        </div>

        {/* Optional date range filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
          <div>
            <label style={{ ...labelStyle, marginBottom: 4 }}>Fra dato (valgfri)</label>
            <input
              type="date"
              style={{ ...inputStyle, width: 160 }}
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label style={{ ...labelStyle, marginBottom: 4 }}>Til dato (valgfri)</label>
            <input
              type="date"
              style={{ ...inputStyle, width: 160 }}
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
          </div>
          {(fromDate || toDate) && (
            <button
              onClick={() => { setFromDate(''); setToDate('') }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 12, padding: '0 4px', alignSelf: 'flex-end', marginBottom: 2 }}
            >
              Nulstil
            </button>
          )}
        </div>

        <Button size="sm" isLoading={downloadingEconomic} onClick={downloadEconomicCSV}>
          <Download size={13} />
          Hent til e-conomic (CSV)
        </Button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main page
// ═══════════════════════════════════════════════════════════════════════════════

export function FinancialListPage() {
  const [activeTab, setActiveTab] = React.useState('fakturaer')

  return (
    <AppLayout>
      <div style={{ padding: '0 0 24px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 3 }}>Økonomi</div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>Fakturaer, betalingssporing og export</div>
        </div>
      </div>

      <div style={{
        background: '#ffffff',
        borderRadius: 10,
        border: '1px solid #e8eaed',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e8eaed', padding: '0 20px' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '13px 16px',
                fontSize: 13,
                fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? '#111827' : '#6b7280',
                background: 'none',
                borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                borderBottom: activeTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color 0.12s, border-color 0.12s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'fakturaer' && <FakturaerTab />}
        {activeTab === 'betalingssporing' && <BetalingssporingTab />}
        {activeTab === 'export' && <ExportTab />}
      </div>
    </AppLayout>
  )
}
